// © Microsoft Corporation. All rights reserved.
import { breakpoints } from "./SSRInteropContractHelpers";
/**
 * The param-name prefix used when serializing params onto the query string.
 */
export const SSRRequestDataPrefix = "rd";
/**
 * `OSATE` (OneService access token exists) is a client readable companions cookie dropped together with the
 * `OSAT`(OneService Access Token) cookie to indicate the existence of the `OSAT` cookie.
 * `OSATE` cookie is usually set to expire 6 months from creation, while `OSAT` expires at token expiry (~1-24h)
 *
 * Thus if `OSATE` cookie exists without the accompanying `OSAT` cookie to conclude that
 * a session exists, but the token has expired, which must be re-acquired/re-validated via client-side JS.
 *
 * The `OSATE` can also be deleted manually during various interactive login/logout scenarios to semantically
 * indicate that the `OSAT` token must be re-established/re-validated on client-side JS.
 *
 * 1) Thus if only one of `OSAT` or `OSATE` exists, that indicates PSSR code cannot render the page and thus must
 *       fallback to client-side rendering.
 * 2) If both are missing that means the user has logged out, or never established
 *       an auth session, which should enable PSSR.
 * 3) If both exists that means the token inside `OSAT` is valid and can be used in PSSR.
 */
export const osatCookieExistsCookieName = "OSATE";
/**
 * OneService Access Token cookie used to autheticate 1S calls under PSSR mode.
 * Expires at the time of token expiry (~1-24hr based on MSO tenant auth configuration)
 * Please see detailed notes under `osatCookieExistsCookieName`
 */
export const osatCookieName = "OSAT";
/**
 * Defines the "interop" contract between client and server-side renderer.
 * All of these values will be serialized into request parameters to be passed
 * on the HTTP request to the SSR server.
 */
export const SSRContract = {
    ver: "0.1.0",
    spec: {
        // IWindow
        innerWidth: {
            clientValue: () => window.innerWidth
        },
        innerHeight: {
            clientValue: () => window.innerHeight
        },
        outerWidth: {
            clientValue: () => window.outerWidth
        },
        breakpoint: {
            clientValue: () => identifyBreakpoint(window.innerWidth, breakpoints)
        },
        locationHash: {
            clientValue: () => window.location.hash
        },
        locationHref: {
            clientValue: () => window.location.href
        },
        locationHost: {
            clientValue: () => window.location.host
        },
        locationHostName: {
            clientValue: () => window.location.hostname
        },
        locationOrigin: {
            clientValue: () => window.location.origin
        },
        locationPathName: {
            clientValue: () => window.location.pathname
        },
        locationProtocol: {
            clientValue: () => window.location.protocol
        },
        locationSearch: {
            clientValue: () => window.location.search
        },
        screenTop: {
            clientValue: () => window.screenTop
        },
        screenY: {
            clientValue: () => window.screenY
        },
        // IBrowser
        canUseCssGrid: {
            clientValue: () => ((window.CSS && window.CSS.supports && CSS.supports("display", "grid")) || false)
        },
        // IDevice
        devicePixelRatio: {
            clientValue: () => window.devicePixelRatio
        },
        // ILocale
        language: {
            clientValue: () => document.documentElement.lang
        },
        direction: {
            clientValue: () => document.documentElement.dir
        }
    }
};
/**
 * Identifies current breakpoint based on window width. Same implementation as ""@microsoft/fast-layouts-react""
 * The package has been deprecated and prevented dependency projects to use react version later than 16. So removing these dependencies.
 */
function identifyBreakpoint(windowWidth, breakpoints) {
    for (let breakpoint = breakpoints.length - 1; breakpoint >= 0; breakpoint--) {
        if (windowWidth >= breakpoints[breakpoint]) {
            return breakpoint;
        }
    }
}
//# sourceMappingURL=SSRInteropContract.js.map